import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import { TagsField } from '@/features/operations/filters/TagsField';
import type { AdminApplicationEgressIpsResponse } from '@/api/models';
import { isValidIpAddress } from './ipAddress';

interface ApplicationDeclaredEgressIpsDialogProps {
  open: boolean;
  applicationCode: string;
  declaredIps: string[];
  onClose: () => void;
  onSaved: (response: AdminApplicationEgressIpsResponse) => void;
}

/** `PUT /v1/admin/applications/{id}/declared-egress-ips` — replaces the declared set in full. */
export function ApplicationDeclaredEgressIpsDialog({
  open,
  applicationCode,
  declaredIps,
  onClose,
  onSaved,
}: ApplicationDeclaredEgressIpsDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [ips, setIps] = useState<string[]>(declaredIps);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setIps(declaredIps);
      setAccessDenied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await payApiClient.updateApplicationDeclaredEgressIps(applicationCode, { ips });
      notify(translate('pay.applications.egressIps.dialog.success'), { type: 'success' });
      onSaved(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.applications.egressIps.dialog.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TagsField
              label={translate('pay.applications.egressIps.dialog.fieldIps')}
              helperText={translate('pay.applications.egressIps.dialog.fieldIpsHelp')}
              values={ips}
              onChange={setIps}
              validate={isValidIpAddress}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.applications.egressIps.dialog.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {translate('pay.applications.egressIps.dialog.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
